import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap, catchError } from 'rxjs';
import { environment } from '@env/environment';
import { TranslateService } from './translate.service';
import { map } from 'rxjs/operators';
import { CommonToastService } from '@common2/services/toast.service';
export interface NotificationItem {
    id: number;
    title: string;
    message: string;
    type: string;
    extra?: any;
    read: boolean;
    createdAt: any;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
    private originalTitle: string = document.title;
    private userHasInteracted = false;
    private titleInterval: any;
    private notifications$ = new BehaviorSubject<NotificationItem[]>([]);
    private unreadCount$ = new BehaviorSubject<number>(0);

    constructor(
        private http: HttpClient,
        private translate: TranslateService,
        private toastService: CommonToastService
    ) {
        this.registerVisibilityHandler();
        this.captureFirstInteraction();
    }

    private getAuthHeaders(): HttpHeaders {
        const token = sessionStorage.getItem(environment.tokenSessionName);
        return new HttpHeaders({
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        });
    }
    public setOriginalTitle(title: string) {
        this.originalTitle = title;
    }

    getNotifications(): Observable<NotificationItem[]> {
        return this.notifications$.asObservable();
    }

    getUnreadCount(): Observable<number> {
        return this.unreadCount$.asObservable();
    }
    newNotification(): void {
        if (document.hidden) {
            this.startTitleAnimation();
            this.playNotificationSound();
        }
    }

    fetchNotifications(): void {
        console.log('fetching notifications');
        const url = `${environment.envVar.API_BROADCAST}/api/notifications/unread`;
        this.http
            .get<NotificationItem[]>(url, {
                headers: this.getAuthHeaders(),
            })
            .pipe(
                tap((data) => {
                    console.log(data);
                    //this.notifications$.next(data);
                    const formatted = data.map((n) => ({
                        ...n,
                        createdAt: new Date(n.createdAt), // 🔁 transformación crítica
                    }));

                    console.log(formatted);
                    this.notifications$.next(formatted);
                    this.unreadCount$.next(data.filter((n) => !n.read).length);
                }),
                catchError(() => {
                    this.notifications$.next([]);
                    this.unreadCount$.next(0);
                    return of([]);
                })
            )
            .subscribe();
    }

    markAsRead(id: number): void {
        const url = `${environment.envVar.API_BROADCAST}/api/notification/${id}/read`;

        this.http
            .put<{ message: string }>(
                url,
                {},
                { headers: this.getAuthHeaders(), observe: 'response' }
            )
            .pipe(
                tap((response) => {
                    const message = this.translate.instant(
                        response.body?.message
                    );
                    const updated = this.notifications$.value.map((n) =>
                        n.id === id ? { ...n, read: true } : n
                    );
                    this.notifications$.next(updated);
                    this.unreadCount$.next(
                        updated.filter((n) => !n.read).length
                    );
                    this.toastService.onSuccess(message);
                }),
                catchError((error) => {
                    const status = error.status;
                    const message = this.translate.instant(
                        error?.error?.message
                    );

                    if ([409].includes(status)) {
                        this.toastService.onError(message);
                    } else {
                        console.error('🛑 Not handled error:', error);
                        const messageh = this.translate.instant(
                            'words.something-wrong'
                        );
                        this.toastService.onError(messageh);
                    }

                    return of(null); // evita que el error se propague
                })
            )
            .subscribe();
    }

    markAllAsRead(): void {
        const url = `${environment.envVar.API_BROADCAST}/api/notifications/read-all`;

        this.http
            .put<{ message: string }>(
                url,
                {},
                {
                    headers: this.getAuthHeaders(),
                    observe: 'response',
                }
            )
            .pipe(
                tap((response) => {
                    const message = this.translate.instant(
                        response.body?.message
                    );

                    const updated = this.notifications$.value.map((n) => ({
                        ...n,
                        read: true,
                    }));
                    this.notifications$.next(updated);
                    this.unreadCount$.next(0);

                    this.toastService.onSuccess(message);
                }),
                catchError((error) => {
                    const status = error.status;
                    const message = this.translate.instant(
                        'words.something-wrong'
                    );

                    console.error(
                        `🛑 Code ${status} No handled on markAllAsRead:`,
                        error
                    );
                    this.toastService.onError(message);

                    return of(null);
                })
            )
            .subscribe();
    }

    pushNotification(n: NotificationItem): void {
        const current = this.notifications$.value;
        this.notifications$.next([n, ...current]);
        this.unreadCount$.next(this.unreadCount$.value + 1);
    }
    private playNotificationSound(): void {
        if (!this.userHasInteracted) return;

        const audio = new Audio('assets/sounds/notification.mp3');
        audio.play().catch((err) => {
            console.warn(
                'No se pudo reproducir el sonido de notificación:',
                err
            );
        });
    }

    private registerVisibilityHandler(): void {
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                this.stopTitleAnimation();
            }
        });
    }

    private captureFirstInteraction(): void {
        document.addEventListener(
            'click',
            () => {
                this.userHasInteracted = true;
            },
            { once: true }
        );
    }
    startTitleAnimation(): void {
        const frames = ['🔈', '🔉', '🔊'];
        let i = 0;

        if (this.titleInterval) return; // ya está corriendo

        this.titleInterval = setInterval(() => {
            const icon = frames[i % frames.length];
            const translated = this.translate.instant('words.notification.tab');
            document.title = `${icon} ${translated} | ${this.originalTitle}`;
            i++;
        }, 500); // cambia cada 500ms para sensación de progresión
    }

    stopTitleAnimation(): void {
        if (this.titleInterval) {
            clearInterval(this.titleInterval);
            this.titleInterval = null;
            document.title = this.originalTitle;
        }
    }
}
